import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGlobalContext } from '~/context/GlobalContext';
import { apiService } from '~/services/apiService';
import styles from '~/styles/AdminPanel/ServerInfo.module.css';
import adminStyles from '~/styles/AdminPanel/AdminPanel.module.css';

interface IServerInfo {
  timestamp: string;
  os: {
    platform: string;
    type: string;
    release: string;
    version: string | null;
    arch: string;
    hostname: string;
    uptimeSeconds: number;
  };
  runtime: {
    nodeVersion: string;
    npmVersion: string | null;
    v8Version: string;
    openSslVersion: string;
  };
  databases: {
    mongo: {
      version: string | null;
      host: string | null;
      readyState: number;
      dbName: string | null;
      storageEngine: string | null;
    };
    redis: {
      version: string | null;
      mode: string | null;
      os: string | null;
      connectedClients: number | null;
      usedMemoryHuman: string | null;
      uptimeSeconds: number | null;
    };
  };
  cpu: {
    model: string | null;
    cores: number;
    usagePercent: number;
    loadAverage: { '1m': number; '5m': number; '15m': number };
  };
  memory: {
    totalBytes: number;
    usedBytes: number;
    freeBytes: number;
    usedPercent: number;
  };
  disk: {
    totalBytes: number;
    usedBytes: number;
    availableBytes: number;
    usedPercent: number;
    mount: string;
  } | null;
  process: {
    pid: number;
    ppid: number;
    masterPid: number;
    clusterMode: boolean;
    clusterRole: string;
    workerId: number | null;
    uptimeSeconds: number;
    memoryRss: number;
    memoryHeapUsed: number;
    memoryHeapTotal: number;
    execPath: string;
    cwd: string;
    environment: string;
  };
}

function formatBytes(bytes: number): string {
  if (!bytes && bytes !== 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  let value = bytes;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i++;
  }
  return `${value.toFixed(value >= 100 || i === 0 ? 0 : 1)} ${units[i]}`;
}

function formatUptime(seconds: number): string {
  if (!seconds && seconds !== 0) return '—';
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  const parts: string[] = [];
  if (d) parts.push(`${d}d`);
  if (h || d) parts.push(`${h}h`);
  if (m || h || d) parts.push(`${m}m`);
  parts.push(`${s}s`);
  return parts.join(' ');
}

function readyStateLabel(state: number): string {
  switch (state) {
    case 0: return 'rozłączony';
    case 1: return 'połączony';
    case 2: return 'łączenie…';
    case 3: return 'rozłączanie…';
    default: return `nieznany (${state})`;
  }
}

function ProgressBar({ percent, label }: { percent: number; label?: string }) {
  const clamped = Math.max(0, Math.min(100, percent));
  const colorClass = clamped >= 90 ? styles.barCritical : clamped >= 70 ? styles.barWarn : styles.barOk;
  return (
    <div className={styles.progressWrap}>
      <div className={styles.progressTrack}>
        <div className={`${styles.progressFill} ${colorClass}`} style={{ width: `${clamped}%` }} />
      </div>
      <span className={styles.progressLabel}>{label ?? `${clamped.toFixed(1)}%`}</span>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className={styles.row}>
      <span className={styles.rowLabel}>{label}</span>
      <span className={styles.rowValue}>{value ?? '—'}</span>
    </div>
  );
}

function ServerInfo() {
  const { context } = useGlobalContext();
  const navigate = useNavigate();

  const [info, setInfo] = useState<IServerInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchInfo = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiService('/admin/server-info');
      setInfo(data as IServerInfo);
    } catch (e: any) {
      setError(e?.message || 'Nie udało się pobrać danych o serwerze.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (context.authUser?.role === 'administrator') {
      fetchInfo();
    }
  }, [context.authUser, fetchInfo]);

  if (!context.authUser || context.authUser.role !== 'administrator') {
    return (
      <div className={adminStyles.accessDenied}>
        <svg width="48" height="48" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
        </svg>
        <h2>Brak dostępu</h2>
        <p>Ta strona jest dostępna tylko dla administratorów.</p>
        <button className={adminStyles.refreshBtn} onClick={() => navigate('/')}>Wróć na stronę główną</button>
      </div>
    );
  }

  return (
    <div className={adminStyles.pageWrapper}>
      <div className={adminStyles.topBar}>
        <button className={adminStyles.backBtn} onClick={() => navigate('/admin')} title="Wróć do panelu admina">
          ←
        </button>
        <span className={adminStyles.topBarTitle}>Informacje o serwerze</span>
        <button
          className={adminStyles.refreshBtn}
          onClick={fetchInfo}
          disabled={loading}
          title="Odśwież dane"
        >
          <span className={loading ? adminStyles.spinning : ''}>↻</span>
          {loading ? ' Odświeżanie…' : ' Odśwież'}
        </button>
      </div>

      <div className={styles.content}>
        {error && <div className={adminStyles.errorBar}>{error}</div>}

        {!info && loading && (
          <div className={styles.loadingState}>Ładowanie informacji o serwerze…</div>
        )}

        {info && (
          <div className={styles.cardsGrid}>
            {/* ── System ── */}
            <section className={styles.card}>
              <h3 className={styles.cardTitle}>System operacyjny</h3>
              <Row label="Typ" value={`${info.os.type} (${info.os.platform})`} />
              <Row label="Wersja jądra" value={info.os.release} />
              {info.os.version && <Row label="Wersja systemu" value={info.os.version} />}
              <Row label="Architektura" value={info.os.arch} />
              <Row label="Hostname" value={info.os.hostname} />
              <Row label="Uptime systemu" value={formatUptime(info.os.uptimeSeconds)} />
            </section>

            {/* ── Runtime ── */}
            <section className={styles.card}>
              <h3 className={styles.cardTitle}>Środowisko uruchomieniowe</h3>
              <Row label="Node.js" value={info.runtime.nodeVersion} />
              <Row label="npm" value={info.runtime.npmVersion ?? 'niedostępne'} />
              <Row label="V8" value={info.runtime.v8Version} />
              <Row label="OpenSSL" value={info.runtime.openSslVersion} />
              <Row label="Tryb" value={info.process.environment} />
            </section>

            {/* ── MongoDB ── */}
            <section className={styles.card}>
              <h3 className={styles.cardTitle}>MongoDB</h3>
              <Row label="Wersja" value={info.databases.mongo.version ?? 'niedostępna'} />
              <Row label="Host" value={info.databases.mongo.host} />
              <Row label="Baza" value={info.databases.mongo.dbName} />
              <Row label="Silnik" value={info.databases.mongo.storageEngine} />
              <Row label="Stan połączenia" value={readyStateLabel(info.databases.mongo.readyState)} />
            </section>

            {/* ── Redis ── */}
            <section className={styles.card}>
              <h3 className={styles.cardTitle}>Redis</h3>
              <Row label="Wersja" value={info.databases.redis.version ?? 'niedostępna'} />
              <Row label="Tryb" value={info.databases.redis.mode} />
              <Row label="OS" value={info.databases.redis.os} />
              <Row label="Połączeni klienci" value={info.databases.redis.connectedClients?.toString()} />
              <Row label="Zużyta pamięć" value={info.databases.redis.usedMemoryHuman} />
              <Row
                label="Uptime"
                value={info.databases.redis.uptimeSeconds != null ? formatUptime(info.databases.redis.uptimeSeconds) : null}
              />
            </section>

            {/* ── CPU ── */}
            <section className={styles.card}>
              <h3 className={styles.cardTitle}>CPU</h3>
              <Row label="Model" value={info.cpu.model} />
              <Row label="Rdzenie" value={info.cpu.cores} />
              <div className={styles.row}>
                <span className={styles.rowLabel}>Użycie</span>
                <ProgressBar percent={info.cpu.usagePercent} label={`${info.cpu.usagePercent.toFixed(1)}%`} />
              </div>
              <Row
                label="Load average"
                value={`${info.cpu.loadAverage['1m'].toFixed(2)} / ${info.cpu.loadAverage['5m'].toFixed(2)} / ${info.cpu.loadAverage['15m'].toFixed(2)}`}
              />
            </section>

            {/* ── RAM ── */}
            <section className={styles.card}>
              <h3 className={styles.cardTitle}>Pamięć RAM</h3>
              <Row label="Łącznie" value={formatBytes(info.memory.totalBytes)} />
              <Row label="Użyte" value={formatBytes(info.memory.usedBytes)} />
              <Row label="Wolne" value={formatBytes(info.memory.freeBytes)} />
              <div className={styles.row}>
                <span className={styles.rowLabel}>Wykorzystanie</span>
                <ProgressBar percent={info.memory.usedPercent} label={`${info.memory.usedPercent.toFixed(1)}%`} />
              </div>
            </section>

            {/* ── Disk ── */}
            <section className={styles.card}>
              <h3 className={styles.cardTitle}>Dysk</h3>
              {info.disk ? (
                <>
                  <Row label="Punkt montowania" value={info.disk.mount} />
                  <Row label="Łącznie" value={formatBytes(info.disk.totalBytes)} />
                  <Row label="Użyte" value={formatBytes(info.disk.usedBytes)} />
                  <Row label="Wolne" value={formatBytes(info.disk.availableBytes)} />
                  <div className={styles.row}>
                    <span className={styles.rowLabel}>Wykorzystanie</span>
                    <ProgressBar percent={info.disk.usedPercent} label={`${info.disk.usedPercent.toFixed(1)}%`} />
                  </div>
                </>
              ) : (
                <div className={styles.row}>Informacje o dysku niedostępne.</div>
              )}
            </section>

            {/* ── Proces ── */}
            <section className={styles.card}>
              <h3 className={styles.cardTitle}>Proces aplikacji</h3>
              <Row label="PID instancji" value={info.process.pid} />
              <Row label="PID rodzica (PPID)" value={info.process.ppid} />
              <Row label="PID głównego (master)" value={info.process.masterPid} />
              <Row label="Tryb cluster" value={info.process.clusterMode ? 'tak' : 'nie'} />
              <Row label="Rola" value={info.process.clusterRole} />
              {info.process.workerId != null && <Row label="ID workera" value={info.process.workerId} />}
              <Row label="Uptime procesu" value={formatUptime(info.process.uptimeSeconds)} />
              <Row label="RSS" value={formatBytes(info.process.memoryRss)} />
              <Row label="Heap (used / total)" value={`${formatBytes(info.process.memoryHeapUsed)} / ${formatBytes(info.process.memoryHeapTotal)}`} />
              <Row label="Katalog roboczy" value={info.process.cwd} />
              <Row label="Plik wykonywalny" value={info.process.execPath} />
            </section>
          </div>
        )}

        {info && (
          <div className={styles.footerNote}>
            Ostatnia aktualizacja: {new Date(info.timestamp).toLocaleString('pl-PL')}
          </div>
        )}
      </div>
    </div>
  );
}

export default ServerInfo;
